import React from 'react';
import { Box, Typography, Link, CircularProgress, useTheme, Tooltip } from '@mui/material';
import WarningFilled from '@ant-design/icons/WarningFilled';

interface RecordLimitWarningProps {
    displayedCount?: number;
    totalRecords: number;
    loading?: boolean;
    onShowAll?: () => void;
}

const RecordLimitWarning: React.FC<RecordLimitWarningProps> = ({ displayedCount = 100, totalRecords, loading = false, onShowAll }) => {
    const theme = useTheme();

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'nowrap', width: '100%', minWidth: 0 }}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: `1px solid ${theme.palette.warning[100]}`,
                    background: theme.palette.warning.lighter,
                    flex: 1,
                    minWidth: 0
                }}
            >
                <WarningFilled style={{ color: theme.palette.warning.main, fontSize: 12, marginTop: '-1px' }} />
                <Tooltip
                    placement="top"
                    arrow
                    title={`Only ${displayedCount} of ${totalRecords} records are displayed. Click Show All to load the remaining records; this may take a few seconds.`}
                >
                    <Typography
                        color="warning"
                        sx={{
                            fontSize: 12,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                        noWrap
                        component="span"
                    >
                        Only {displayedCount} of {totalRecords} records are displayed. Click{' '}
                        <Typography component="span" color="warning" sx={{ fontSize: 12, fontWeight: 500 }}>
                            Show All{' '}
                        </Typography>
                        to load the remaining records; this may take a few seconds.
                    </Typography>
                </Tooltip>
            </Box>
            {loading ? (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <CircularProgress size={12} color="inherit" />
                    <Typography variant="caption">Loading...</Typography>
                </Box>
            ) : (
                <Link component="button" type="button" onClick={onShowAll} sx={{ fontWeight: 500, fontSize: 14, whiteSpace: 'nowrap' }}>
                    Show All
                </Link>
            )}
        </Box>
    );
};

export default React.memo(RecordLimitWarning);
